import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import { Stack, Typography } from '../components/mui-system';
import { api } from '../api/client';
import type {
  DonorCsvDelimiter,
  DonorCsvOptions,
  DonorCsvSample,
  DonorImportPreview,
  DonorImportJob,
} from '../../../api/src/donors/donors.types';

const SAMPLE_BYTES = 65_536;
const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error));
export function DonorImportDialog({
  file,
  workspace,
  onClose,
  onStarted,
}: {
  file: File;
  workspace: string;
  onClose: () => void;
  onStarted: (result: DonorImportJob) => void;
}) {
  const [delimiter, setDelimiter] = useState<DonorCsvDelimiter | ''>('');
  const [hasHeader, setHasHeader] = useState(true);
  const [sample, setSample] = useState<DonorCsvSample | null>(null);
  const [columns, setColumns] = useState<DonorCsvSample['suggested']>({
    article: null,
    target: null,
    anchor: null,
  });
  const [preview, setPreview] = useState<DonorImportPreview | null>(null);
  const [error, setError] = useState('');
  const [sampling, setSampling] = useState(true);
  const [busy, setBusy] = useState(false);
  const prefix = useRef<Promise<string> | null>(null);
  const fullText = useRef<string | null>(null);
  const requestId = useRef<string | null>(null);
  useEffect(() => {
    let current = true;
    // Only this fixed-size slice is read for column selection. Never file.text().
    prefix.current ??= file.slice(0, SAMPLE_BYTES).text();
    void prefix.current
      .then((text) =>
        api.sampleDonorImport(workspace, {
          sample: text,
          complete: file.size <= SAMPLE_BYTES,
          delimiter: delimiter || undefined,
          hasHeader,
        }),
      )
      .then((result) => {
        if (current) {
          setSample(result);
          setColumns(result.suggested);
          setSampling(false);
        }
      })
      .catch((caught: unknown) => {
        if (current) {
          setError(errorText(caught));
          setSampling(false);
        }
      });
    return () => {
      current = false;
    };
  }, [file, workspace, delimiter, hasHeader]);
  function resetSample() {
    setSampling(true);
    setSample(null);
    setPreview(null);
    requestId.current = null;
    setError('');
  }
  const selected = [
    columns.article,
    columns.target,
    ...(columns.anchor === null ? [] : [columns.anchor]),
  ];
  const validMapping =
    sample &&
    columns.article !== null &&
    columns.target !== null &&
    new Set(selected).size === selected.length;
  function options(): DonorCsvOptions {
    return {
      delimiter: sample!.delimiter,
      hasHeader,
      columns: { article: columns.article!, target: columns.target!, anchor: columns.anchor },
    };
  }
  async function validate() {
    setBusy(true);
    setError('');
    setPreview(null);
    requestId.current = null;
    try {
      fullText.current ??= await file.text();
      setPreview(await api.previewDonorImport(workspace, fullText.current, options()));
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  }
  async function importCsv() {
    setBusy(true);
    setError('');
    try {
      requestId.current ??= crypto.randomUUID();
      onStarted(
        await api.importDonors(workspace, fullText.current!, options(), {
          requestId: requestId.current,
          fileName: file.name.slice(0, 255),
        }),
      );
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => !busy && onClose()}>
      <DialogTitle>Импорт доноров из CSV</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <Typography sx={{ overflowWrap: 'anywhere' }}>{file.name}</Typography>
          <Alert severity="info">
            Для выбора колонок читаем только первые 64 КБ. Весь файл прочитаем после нажатия
            «Проверить импорт». После запуска импорт продолжится в фоне, даже если закрыть страницу.
            Архивные статьи пропускаются; восстановить их можно из архива.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              select
              label="Разделитель"
              value={delimiter}
              disabled={busy}
              sx={{ minWidth: 220 }}
              onChange={(event) => {
                resetSample();
                setDelimiter(event.target.value as DonorCsvDelimiter | '');
              }}
            >
              <MenuItem value="">Автоматически</MenuItem>
              <MenuItem value=",">Запятая</MenuItem>
              <MenuItem value=";">Точка с запятой</MenuItem>
              <MenuItem value={'\t'}>Табуляция</MenuItem>
              <MenuItem value="|">Вертикальная черта</MenuItem>
            </TextField>
            <FormControlLabel
              label="Первая строка — заголовки"
              control={
                <Checkbox
                  checked={hasHeader}
                  disabled={busy}
                  onChange={(_, value) => {
                    resetSample();
                    setHasHeader(value);
                  }}
                />
              }
            />
          </Stack>
          {sampling && <Typography>Читаем образец…</Typography>}
          {sample && (
            <>
              <Typography variant="body2">
                Строк данных в образце: {sample.sampledRows}. Разделитель:{' '}
                {sample.delimiter === '\t' ? 'табуляция' : sample.delimiter}. Выберите разные
                колонки для каждого поля.
              </Typography>
              {(['article', 'target', 'anchor'] as const).map((field) => {
                const chosen = sample.columns.find((column) => column.index === columns[field]);
                return (
                  <TextField
                    key={field}
                    select
                    fullWidth
                    disabled={busy}
                    label={
                      {
                        article: 'URL статьи-донора',
                        target: 'Целевой домен или URL',
                        anchor: 'Ожидаемый анкор',
                      }[field]
                    }
                    value={columns[field] ?? ''}
                    helperText={
                      chosen
                        ? `Примеры: ${chosen.examples.join(' · ') || 'нет данных в образце'}`
                        : field === 'anchor'
                          ? 'Без колонки анкора проверяется любой текст ссылки'
                          : 'Обязательная колонка'
                    }
                    slotProps={{ formHelperText: { sx: { overflowWrap: 'anywhere' } } }}
                    onChange={(event) => {
                      setColumns({
                        ...columns,
                        [field]: event.target.value === '' ? null : Number(event.target.value),
                      });
                      setPreview(null);
                      requestId.current = null;
                      setError('');
                    }}
                  >
                    <MenuItem value="">
                      {field === 'anchor' ? 'Не использовать' : 'Выберите колонку'}
                    </MenuItem>
                    {sample.columns.map((column) => (
                      <MenuItem key={column.index} value={column.index}>
                        {column.index + 1}. {column.name}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              })}
            </>
          )}
          {preview && (
            <>
              <Typography>
                Строк: {preview.rows}; статей: {preview.articles}; пустых: {preview.empty}; дублей
                пропущено: {preview.duplicates}; строк с другими ссылками объединено:{' '}
                {preview.mergedRows}.
              </Typography>
              <Alert severity={preview.errors.length ? 'error' : 'success'}>
                {preview.errors.length
                  ? `Ошибок: ${preview.errors.length}. Исправьте файл перед импортом.`
                  : 'Файл проверен. Существующие статьи будут дополнены; заметки и история сохранятся.'}
              </Alert>
              {preview.errors.slice(0, 30).map((row, index) => (
                <Typography key={index}>
                  Строка {row.line}: {row.message}
                </Typography>
              ))}
              {preview.sample.map((row, index) => (
                <Typography key={index} fontSize={12} sx={{ overflowWrap: 'anywhere' }}>
                  {row.domain} → {row.target} · {row.anchor || 'любой анкор'}
                </Typography>
              ))}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        <Button disabled={busy} onClick={onClose}>
          Отмена
        </Button>
        <Button disabled={busy || sampling || !validMapping} onClick={() => void validate()}>
          Проверить импорт
        </Button>
        <Button
          variant="contained"
          disabled={busy || !preview?.articles || Boolean(preview?.errors.length)}
          onClick={() => void importCsv()}
        >
          {busy ? 'Обрабатываем…' : 'Импортировать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
